import {
  conversationHandoffStates,
  conversationStatuses,
  isAdminConversationInboxItem,
  isAdminConversationInboxPage,
  isCanonicalChatTimestamp,
  leadAssignableRoles,
  type AdminConversationAssignmentRequest,
  type AdminConversationHandoffRequest,
  type AdminConversationInboxItem,
  type AdminConversationInboxPage,
  type AdminConversationStatusRequest,
  type AdminLeadAssignee,
  type AdminLeadAssigneeDirectoryPage,
  type ConversationHandoffState,
  type ConversationStatus,
} from "@whitehaven/contracts";
import type {
  AdminConversationAssignmentMutationOutcome,
  AdminConversationHandoffMutationOutcome,
  AdminConversationStatusMutationOutcome,
} from "./admin-conversation-gateway";

export const ADMIN_CONVERSATION_UNASSIGNED_VALUE = "__unassigned__";

export const adminConversationStatusLabels = {
  OPEN: "Open",
  WAITING_FOR_STAFF: "Waiting for staff",
  IN_PROGRESS: "In progress",
  CLOSED: "Closed",
  SPAM: "Spam",
} as const satisfies Readonly<Record<ConversationStatus, string>>;

export const adminConversationHandoffLabels = {
  NONE: "No handoff",
  REQUESTED: "Human follow-up requested",
  ACKNOWLEDGED: "Human follow-up acknowledged",
  RESOLVED: "Human follow-up resolved",
} as const satisfies Readonly<Record<ConversationHandoffState, string>>;

const statusTargets = {
  OPEN: ["WAITING_FOR_STAFF", "IN_PROGRESS", "CLOSED", "SPAM"],
  WAITING_FOR_STAFF: ["IN_PROGRESS", "CLOSED", "SPAM"],
  IN_PROGRESS: ["WAITING_FOR_STAFF", "CLOSED", "SPAM"],
  CLOSED: [],
  SPAM: [],
} as const satisfies Readonly<
  Record<ConversationStatus, readonly ConversationStatus[]>
>;

const handoffTargets = {
  NONE: null,
  REQUESTED: "ACKNOWLEDGED",
  ACKNOWLEDGED: "RESOLVED",
  RESOLVED: null,
} as const satisfies Readonly<
  Record<ConversationHandoffState, ConversationHandoffState | null>
>;

function isObject(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactOwnKeys(
  value: object,
  expectedKeys: readonly string[],
): boolean {
  const keys = Reflect.ownKeys(value);
  return (
    keys.length === expectedKeys.length &&
    keys.every((key) => typeof key === "string" && expectedKeys.includes(key))
  );
}

function isSafeIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= 128 &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

function isNullableIdentifier(value: unknown): value is string | null {
  return value === null || isSafeIdentifier(value);
}

function isRevision(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 2_147_483_647
  );
}

function isKnownStatus(value: unknown): value is ConversationStatus {
  return (
    typeof value === "string" &&
    (conversationStatuses as readonly string[]).includes(value)
  );
}

function isKnownHandoffState(
  value: unknown,
): value is ConversationHandoffState {
  return (
    typeof value === "string" &&
    (conversationHandoffStates as readonly string[]).includes(value)
  );
}

function isConfirmedOutcome(
  value: unknown,
): value is { readonly kind: "confirmed"; readonly snapshot: unknown } {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, ["kind", "snapshot"]) &&
    value.kind === "confirmed"
  );
}

export function isAdminConversationFailureOutcome(
  value: unknown,
): value is { readonly kind: "conflict" | "rejected" } {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, ["kind"]) &&
    (value.kind === "conflict" || value.kind === "rejected")
  );
}

export function getAdminConversationStatusTargets(
  status: ConversationStatus,
): readonly ConversationStatus[] {
  return statusTargets[status];
}

export function getAdminConversationHandoffTarget(
  state: ConversationHandoffState,
): ConversationHandoffState | null {
  return handoffTargets[state];
}

export function getAdminConversationHandoffActionLabel(
  state: ConversationHandoffState,
): string | null {
  switch (state) {
    case "NONE":
      return null;
    case "REQUESTED":
      return "Acknowledge human follow-up";
    case "ACKNOWLEDGED":
      return "Resolve human follow-up";
    case "RESOLVED":
      return null;
  }
}

export function isAdminConversationInboxPageConsistent(
  page: unknown,
): page is AdminConversationInboxPage {
  return isAdminConversationInboxPage(page);
}

export function mergeAdminConversationInboxItems(
  current: readonly AdminConversationInboxItem[],
  incoming: readonly AdminConversationInboxItem[],
): readonly AdminConversationInboxItem[] | null {
  const items = [...current, ...incoming];
  const ids = new Set<string>();

  for (const item of items) {
    if (!isAdminConversationInboxItem(item) || ids.has(item.conversationId)) {
      return null;
    }
    ids.add(item.conversationId);
  }

  return items.every((item, index) => {
    const previous = items[index - 1];
    return (
      previous === undefined ||
      previous.updatedAt > item.updatedAt ||
      (previous.updatedAt === item.updatedAt &&
        previous.conversationId < item.conversationId)
    );
  })
    ? items
    : null;
}

function isAdminConversationAssignee(
  value: unknown,
): value is AdminLeadAssignee {
  return (
    isObject(value) &&
    hasExactOwnKeys(value, ["userId", "displayLabel", "role"]) &&
    isSafeIdentifier(value.userId) &&
    typeof value.displayLabel === "string" &&
    value.displayLabel.trim() === value.displayLabel &&
    value.displayLabel.length >= 1 &&
    value.displayLabel.length <= 120 &&
    typeof value.role === "string" &&
    (leadAssignableRoles as readonly string[]).includes(value.role)
  );
}

export function isAdminConversationAssigneeDirectoryPageConsistent(
  value: unknown,
): value is AdminLeadAssigneeDirectoryPage {
  if (
    !isObject(value) ||
    !hasExactOwnKeys(value, ["items", "nextCursor"]) ||
    !Array.isArray(value.items) ||
    value.items.length > 50 ||
    (value.nextCursor !== null && !isSafeIdentifier(value.nextCursor))
  ) {
    return false;
  }

  const seen = new Set<string>();
  return value.items.every((item) => {
    if (!isAdminConversationAssignee(item) || seen.has(item.userId)) {
      return false;
    }
    seen.add(item.userId);
    return true;
  });
}

function currentMatchesAssignmentRequest(
  current: AdminConversationInboxItem,
  request: AdminConversationAssignmentRequest,
): boolean {
  return (
    current.conversationId === request.conversationId &&
    current.assignedUserId === request.expectedAssigneeUserId &&
    current.assignmentRevision === request.expectedAssignmentRevision &&
    current.updatedAt === request.expectedUpdatedAt
  );
}

function currentMatchesStatusRequest(
  current: AdminConversationInboxItem,
  request: AdminConversationStatusRequest,
): boolean {
  return (
    current.conversationId === request.conversationId &&
    current.status === request.expectedStatus &&
    current.statusRevision === request.expectedStatusRevision &&
    current.updatedAt === request.expectedUpdatedAt
  );
}

function currentMatchesHandoffRequest(
  current: AdminConversationInboxItem,
  request: AdminConversationHandoffRequest,
): boolean {
  return (
    current.conversationId === request.conversationId &&
    current.handoffState === request.expectedState &&
    current.handoffRevision === request.expectedHandoffRevision &&
    current.updatedAt === request.expectedUpdatedAt
  );
}

export function applyConfirmedAdminConversationAssignment(
  current: AdminConversationInboxItem,
  request: AdminConversationAssignmentRequest,
  outcome: AdminConversationAssignmentMutationOutcome,
): AdminConversationInboxItem | null {
  if (
    !currentMatchesAssignmentRequest(current, request) ||
    !isConfirmedOutcome(outcome)
  ) {
    return null;
  }

  const snapshot = outcome.snapshot;
  if (
    !isObject(snapshot) ||
    !hasExactOwnKeys(snapshot, [
      "conversationId",
      "assignedUserId",
      "assignmentRevision",
      "updatedAt",
    ]) ||
    snapshot.conversationId !== request.conversationId ||
    !isNullableIdentifier(snapshot.assignedUserId) ||
    snapshot.assignedUserId !== request.targetAssigneeUserId ||
    !isRevision(snapshot.assignmentRevision) ||
    snapshot.assignmentRevision !== request.expectedAssignmentRevision + 1 ||
    !isCanonicalChatTimestamp(snapshot.updatedAt) ||
    snapshot.updatedAt <= request.expectedUpdatedAt
  ) {
    return null;
  }

  const updated = {
    ...current,
    assignedUserId: snapshot.assignedUserId,
    assignmentRevision: snapshot.assignmentRevision,
    updatedAt: snapshot.updatedAt,
  };
  return isAdminConversationInboxItem(updated) ? updated : null;
}

export function applyConfirmedAdminConversationStatus(
  current: AdminConversationInboxItem,
  request: AdminConversationStatusRequest,
  outcome: AdminConversationStatusMutationOutcome,
): AdminConversationInboxItem | null {
  if (
    !currentMatchesStatusRequest(current, request) ||
    !getAdminConversationStatusTargets(request.expectedStatus).includes(
      request.targetStatus,
    ) ||
    !isConfirmedOutcome(outcome)
  ) {
    return null;
  }

  const snapshot = outcome.snapshot;
  if (
    !isObject(snapshot) ||
    !hasExactOwnKeys(snapshot, [
      "conversationId",
      "status",
      "statusRevision",
      "updatedAt",
    ]) ||
    snapshot.conversationId !== request.conversationId ||
    !isKnownStatus(snapshot.status) ||
    snapshot.status !== request.targetStatus ||
    !isRevision(snapshot.statusRevision) ||
    snapshot.statusRevision !== request.expectedStatusRevision + 1 ||
    !isCanonicalChatTimestamp(snapshot.updatedAt) ||
    snapshot.updatedAt <= request.expectedUpdatedAt
  ) {
    return null;
  }

  const updated = {
    ...current,
    status: snapshot.status,
    statusRevision: snapshot.statusRevision,
    updatedAt: snapshot.updatedAt,
  };
  return isAdminConversationInboxItem(updated) ? updated : null;
}

export function applyConfirmedAdminConversationHandoff(
  current: AdminConversationInboxItem,
  request: AdminConversationHandoffRequest,
  outcome: AdminConversationHandoffMutationOutcome,
): AdminConversationInboxItem | null {
  if (
    !currentMatchesHandoffRequest(current, request) ||
    handoffTargets[request.expectedState] !== request.targetState ||
    !isConfirmedOutcome(outcome)
  ) {
    return null;
  }

  const snapshot = outcome.snapshot;
  if (
    !isObject(snapshot) ||
    !hasExactOwnKeys(snapshot, [
      "conversationId",
      "handoffState",
      "handoffRevision",
      "updatedAt",
    ]) ||
    snapshot.conversationId !== request.conversationId ||
    !isKnownHandoffState(snapshot.handoffState) ||
    snapshot.handoffState !== request.targetState ||
    !isRevision(snapshot.handoffRevision) ||
    snapshot.handoffRevision !== request.expectedHandoffRevision + 1 ||
    !isCanonicalChatTimestamp(snapshot.updatedAt) ||
    snapshot.updatedAt <= request.expectedUpdatedAt
  ) {
    return null;
  }

  const updated = {
    ...current,
    handoffState: snapshot.handoffState,
    handoffRevision: snapshot.handoffRevision,
    updatedAt: snapshot.updatedAt,
  };
  return isAdminConversationInboxItem(updated) ? updated : null;
}
